import React, { useState, useRef, useEffect } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export function TwoFactorVerification({ 
  userId, 
  email, 
  deviceId,
  onSuccess, 
  onCancel 
}) {
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState("");
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  const inputRefs = useRef([]);

  // Cooldown Timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Auto-Focus auf erstes Feld
  useEffect(() => {
    if (!useBackupCode && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [useBackupCode]);

  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Nur Zahlen
    
    const newCode = [...code];
    newCode[index] = value.slice(-1); // Nur letzte Ziffer
    setCode(newCode);
    setError("");

    // Auto-Focus zum nächsten Feld
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-Submit wenn alle Felder gefüllt
    if (newCode.every(c => c) && newCode.join("").length === 6) {
      handleVerify(newCode.join(""));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pastedData.length === 6) {
      const newCode = pastedData.split("");
      setCode(newCode);
      handleVerify(pastedData);
    }
  };

  const handleVerify = async (codeToVerify) => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API_BASE}/api/auth/verify-2fa`, {
        user_id: userId,
        code: codeToVerify,
        device_id: deviceId,
        trust_device: true
      });

      // Erfolgreiche Verifikation
      if (response.data.backup_code_warning) {
        // Warnung bei Backup-Code-Nutzung anzeigen
        alert(response.data.backup_code_warning);
      }
      
      onSuccess(response.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Verifikation fehlgeschlagen";
      setError(detail);
      setCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleBackupCodeVerify = async () => {
    if (!backupCode.trim()) {
      setError("Bitte Notfall-Code eingeben");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API_BASE}/api/auth/verify-2fa`, {
        user_id: userId,
        code: backupCode.trim(),
        device_id: deviceId,
        trust_device: true
      });

      if (response.data.backup_code_warning) {
        alert(response.data.backup_code_warning);
      }
      
      onSuccess(response.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Notfall-Code ungültig";
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    setError("");

    try {
      await axios.post(`${API_BASE}/api/auth/resend-2fa-code`, { user_id: userId });
      setResendCooldown(60); // 60 Sekunden Cooldown
    } catch (e) {
      setError("Fehler beim Senden des Codes");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Zwei-Faktor-Authentifizierung</h2>
            <p className="text-slate-500 mt-2">
              {useBackupCode 
                ? "Geben Sie einen Ihrer Notfall-Codes ein"
                : `Ein 6-stelliger Code wurde an ${email} gesendet`
              }
            </p>
          </div>

          {/* Fehler-Anzeige */}
          {error && (
            <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {!useBackupCode ? (
            <>
              {/* Code-Eingabe */}
              <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    disabled={loading}
                    className="w-12 h-14 text-center text-2xl font-bold border-2 border-slate-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all disabled:bg-slate-100"
                  />
                ))}
              </div>

              {/* Code erneut senden */}
              <div className="text-center mb-6">
                <button
                  onClick={handleResendCode}
                  disabled={resending || resendCooldown > 0}
                  className="text-sm text-blue-600 hover:text-blue-800 disabled:text-slate-400"
                >
                  {resending 
                    ? "Sende..." 
                    : resendCooldown > 0 
                      ? `Erneut senden in ${resendCooldown}s` 
                      : "Code erneut senden"
                  }
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Backup-Code Eingabe */}
              <div className="mb-6">
                <input
                  type="text"
                  value={backupCode}
                  onChange={(e) => {
                    setBackupCode(e.target.value.toUpperCase());
                    setError("");
                  }}
                  placeholder="XXXX-XXXX"
                  disabled={loading}
                  className="w-full text-center text-xl font-mono tracking-widest border-2 border-slate-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                />
              </div>

              <button
                onClick={handleBackupCodeVerify}
                disabled={loading || !backupCode.trim()}
                className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors mb-4"
              >
                {loading ? "Überprüfe..." : "Notfall-Code verwenden"}
              </button>
            </>
          )}

          {/* Toggle zwischen Code und Backup-Code */}
          <div className="border-t border-slate-200 pt-4">
            <button
              onClick={() => {
                setUseBackupCode(!useBackupCode);
                setError("");
                setCode(["", "", "", "", "", ""]);
                setBackupCode("");
              }}
              className="w-full text-center text-sm text-slate-600 hover:text-slate-900"
            >
              {useBackupCode 
                ? "← Zurück zur Code-Eingabe" 
                : "Notfall-Code verwenden"
              }
            </button>
          </div>

          {/* Abbrechen */}
          <div className="mt-4 text-center">
            <button
              onClick={onCancel}
              className="text-sm text-slate-500 hover:text-slate-700"
            >
              Anmeldung abbrechen
            </button>
          </div>
        </div>

        {/* Hinweis */}
        <p className="text-center text-xs text-slate-500 mt-4">
          Der Code ist 10 Minuten gültig. Bei falschem Code wird automatisch ein neuer gesendet.
        </p>
      </div>
    </div>
  );
}
